import api from './api';

const API_BASE = '/options';

export type EngineLogCategory =
  | 'OPENED'
  | 'REJECTED'
  | 'NO_SIGNAL'
  | 'CAP_REACHED'
  | 'EXIT'
  | 'SQUARE_OFF'
  | 'LIFECYCLE'
  | 'ERROR';

export interface EngineLogRow {
  id: string;
  timestamp: string;
  state: string;
  category: EngineLogCategory;
  accountId: string | null;
  accountName: string | null;
  strategy: string | null;
  scenario: string | null;
  message: string;
  pnl: number | null;
  data?: any;
}

export interface EngineLogsResponse {
  date: string;
  viewerAccountId: string;
  session: { sessionId: string; currentState: string; tradesCount: number; startTime?: string; endTime?: string } | null;
  rows: EngineLogRow[];
  accounts: Array<{ id: string; name: string }>;
}

export interface OptionQuote {
  contractId: string;
  tradingSymbol?: string;
  strike: number;
  optionType: 'CE' | 'PE';
  ltp: number;
  bid: number;
  ask: number;
  oi: number;
  oiChange?: number;
  volume?: number;
  iv?: number;
  delta?: number;
  gamma?: number;
  theta?: number;
  vega?: number;
}

export interface OptionChainData {
  market: string;
  underlying: string;
  spot: number;
  expiry: string;
  atmStrike: number;
  strikes: number[];
  calls: Record<number, OptionQuote>;
  puts: Record<number, OptionQuote>;
  timestamp: string;
  pcr?: number;
  maxPain?: number;
}

export interface SpotData {
  spot: number;
  open: number;
  high: number;
  low: number;
  previousClose: number;
  gapPct: number;
  dayChangePct?: number;
  vwap?: number;
  openingRangeHigh?: number;
  openingRangeLow?: number;
  lastUpdated: string;
}

export interface KillSwitchStatus {
  currentLevel: string;
  updatedAt: string;
  changedBy: string;
  reason: string;
}

export interface GateReason {
  name: string;
  category: 'REGIME' | 'VOLATILITY' | 'LIQUIDITY' | 'EVENT_RISK' | 'RISK_CEILING';
  passed: boolean;
  actualValue: string | number;
  requiredValue: string | number;
  message: string;
}

export interface NoTradeDecision {
  strategyName: string;
  decision: 'REJECTED' | 'ELIGIBLE' | 'BLOCKED_BY_RISK';
  timestamp: string;
  spotPrice: number;
  nextEvaluationTime: string;
  gates: GateReason[];
  summaryMessage: string;
}

export interface StrategyTimelineEvent {
  id: string;
  timestamp: string;
  timeFormatted: string;
  type: string;
  status: 'SUCCESS' | 'WARNING' | 'ALERT' | 'INFO';
  title: string;
  details: string;
  strategyId?: string;
  pnlSnapshot?: number;
}

export interface SystemHealthComponent {
  id: string;
  name: string;
  category: 'INFRASTRUCTURE' | 'FEED' | 'EXECUTION' | 'RISK_GOVERNANCE';
  status: 'HEALTHY' | 'WARNING' | 'CRITICAL' | 'LOCKED';
  latencyMs?: number;
  details: string;
  isCritical: boolean;
  lastChecked: string;
}

export interface OptionsSystemHealthReport {
  overallStatus: 'HEALTHY' | 'DEGRADED' | 'CRITICAL';
  isEntriesAllowed: boolean;
  lockoutReason?: string;
  timestamp: string;
  activeMode: string;
  components: SystemHealthComponent[];
}

export interface NiftyOptionsModeConfig {
  enabled: boolean;
  mode: 'DISABLED' | 'OBSERVE' | 'SHADOW' | 'PAPER' | 'LIVE';
  broker: 'KITE' | 'SHADOW_BROKER' | 'PAPER_BROKER';
  autoEntry: boolean;
  autoAdjust: boolean;
  forceSquareOff: boolean;
  maxDailyLoss: number;
  maxMarginPct: number;
  capitalRampLots: number;
  safety?: {
    valid: boolean;
    errors: string[];
  };
}

export const optionsService = {
  async getSpot(underlying = 'NIFTY'): Promise<SpotData> {
    const res = await api.get(`${API_BASE}/${underlying}/spot`);
    return res.data;
  },

  async getChain(underlying = 'NIFTY', expiry?: string): Promise<OptionChainData> {
    const params = expiry ? { expiry } : {};
    const res = await api.get(`${API_BASE}/${underlying}/chain`, { params });
    return res.data;
  },

  async getExpiries(underlying = 'NIFTY'): Promise<{ expiries: string[] }> {
    const res = await api.get(`${API_BASE}/${underlying}/expiries`);
    return res.data;
  },

  async getKillSwitchStatus(): Promise<KillSwitchStatus> {
    const res = await api.get(`${API_BASE}/kill-switch/status`);
    return res.data;
  },

  async setKillSwitchLevel(level: string, reason = 'Manual UI adjustment', changedBy = 'DASHBOARD_USER'): Promise<KillSwitchStatus> {
    const res = await api.post(`${API_BASE}/kill-switch/set-level`, { level, reason, changedBy });
    return res.data;
  },

  async flattenPositions(reason = 'Emergency flatten clicked'): Promise<KillSwitchStatus> {
    const res = await api.post(`${API_BASE}/kill-switch/flatten`, { reason, changedBy: 'DASHBOARD_USER' });
    return res.data;
  },

  /** Latest published (offline-computed) qualification report for a strategy. */
  async getReadiness(strategy = 'SHORT_STRANGLE', version = '1.0.0'): Promise<any> {
    const res = await api.get(`${API_BASE}/deployment/readiness`, { params: { strategy, version } });
    return res.data;
  },

  async getShadowStatus(): Promise<any> {
    const res = await api.get(`${API_BASE}/deployment/shadow-status`);
    return res.data;
  },

  async getMode(): Promise<NiftyOptionsModeConfig> {
    const res = await api.get(`${API_BASE}/mode`);
    return res.data;
  },

  async getNoTradeDecision(strategy = 'SHORT_STRANGLE'): Promise<NoTradeDecision> {
    const res = await api.get(`${API_BASE}/no-trade`, { params: { strategy } });
    return res.data;
  },

  async getTimeline(limit = 50): Promise<StrategyTimelineEvent[]> {
    const res = await api.get(`${API_BASE}/timeline`, { params: { limit } });
    return res.data;
  },

  async getCommandCenter(underlying = 'NIFTY'): Promise<CommandCenterData> {
    const res = await api.get(`${API_BASE}/command-center`, { params: { underlying } });
    return res.data;
  },

  async getDashboardOverview(date?: string, mode?: string, underlying = 'NIFTY'): Promise<any> {
    const res = await api.get(`${API_BASE}/dashboard-overview`, { params: { date, mode, underlying } });
    return res.data;
  },

  async getSessionToday(): Promise<any> {
    const res = await api.get(`${API_BASE}/session/today`);
    return res.data;
  },

  async getEngineLogs(date?: string): Promise<EngineLogsResponse> {
    const res = await api.get(`${API_BASE}/logs`, { params: { date } });
    return res.data;
  },

  async getPositions(underlying = 'NIFTY'): Promise<{ activeCount: number; activePositions: any[]; history: any[] }> {
    const res = await api.get(`${API_BASE}/${underlying}/positions`);
    return res.data;
  },

  async exitPosition(positionId: string, reason = 'MANUAL'): Promise<any> {
    const res = await api.post(`${API_BASE}/positions/${encodeURIComponent(positionId)}/exit`, { reason });
    return res.data;
  },

  async getStrategies(underlying = 'NIFTY'): Promise<{ underlying: string; market: string; signals: any[] }> {
    const res = await api.get(`${API_BASE}/${underlying}/strategies`);
    return res.data;
  },

  async runBacktest(params: any, underlying = 'NIFTY'): Promise<any> {
    const res = await api.post(`${API_BASE}/${underlying}/backtest/run`, params);
    return res.data;
  },

  async getBacktests(underlying = 'NIFTY'): Promise<{ count: number; runs: any[] }> {
    const res = await api.get(`${API_BASE}/${underlying}/backtests`);
    return res.data;
  },

  async getSystemHealth(): Promise<OptionsSystemHealthReport> {
    const res = await api.get(`${API_BASE}/system-health`);
    return res.data;
  },

  async getDatasetCoverage(underlying = 'NIFTY'): Promise<{ underlying: string; totalFiles: number; years: Array<{ year: string; fileCount: number; status: string }> }> {
    const res = await api.get(`${API_BASE}/${underlying}/dataset-coverage`);
    return res.data;
  },

  async getStrategyVersions(): Promise<{ versions: any[] }> {
    const res = await api.get(`${API_BASE}/strategy-versions`);
    return res.data;
  },

  async getVolatility(underlying = 'NIFTY'): Promise<any> {
    const res = await api.get(`${API_BASE}/${underlying}/volatility`);
    return res.data;
  },

  async getRegime(underlying = 'NIFTY'): Promise<any> {
    const res = await api.get(`${API_BASE}/${underlying}/regime`);
    return res.data;
  },

  async getStructure(underlying = 'NIFTY'): Promise<MultiTimeframeStructure> {
    const res = await api.get(`${API_BASE}/${underlying}/structure`);
    return res.data;
  },

  async getSetup(underlying = 'NIFTY', lots = 1): Promise<StrategySetupProposal> {
    const res = await api.get(`${API_BASE}/${underlying}/setup`, { params: { lots } });
    return res.data;
  },

  async getOptionsSettings(): Promise<OptionsUserSettings> {
    const res = await api.get(`${API_BASE}/settings`);
    return res.data;
  },

  async updateOptionsSettings(partial: Partial<OptionsUserSettings>): Promise<OptionsUserSettings> {
    const res = await api.put(`${API_BASE}/settings`, partial);
    return res.data;
  },
};

export interface OptionsUserSettings {
  autoTradingEnabled: boolean;
  liveTradingEnabled: boolean;
  capitalAllocationInr: number;
  maxDailyLossInr: number;
  maxActivePositions: number;
  maxPositionRiskInr: number;
  /** Deployment-wide master switch — a user's own liveTradingEnabled only takes effect when this is also true. */
  liveTradingMasterSwitch: boolean;
}

export interface StructureZone {
  type: 'SUPPLY' | 'DEMAND';
  upper: number;
  lower: number;
  mid: number;
  touches: number;
  strength: number;
  createdAt: string;
}

export interface SwingPoint {
  type: 'HIGH' | 'LOW';
  price: number;
  timestamp: string;
  index: number;
}

export interface TimeframeStructure {
  timeframe: string;
  bias: 'BULLISH' | 'BEARISH' | 'RANGE';
  event: 'CONTINUATION' | 'REVERSAL_CHOCH' | 'RANGE';
  swings: SwingPoint[];
  lastSwingHigh: SwingPoint | null;
  lastSwingLow: SwingPoint | null;
  chochLevel: number | null;
  reasons: string[];
}

export interface ConfirmationResult {
  confirmed: boolean;
  direction: 'BULLISH' | 'BEARISH' | 'NONE';
  patterns: string[];
  reasons: string[];
  candleTime: string | null;
}

export interface MultiTimeframeStructure {
  underlying: string;
  spot: number;
  timestamp: string;
  macro: {
    bias: 'BULLISH' | 'BEARISH' | 'RANGE';
    supplyZones: StructureZone[];
    demandZones: StructureZone[];
    nearestSupply: StructureZone | null;
    nearestDemand: StructureZone | null;
  };
  primary: TimeframeStructure;
  confirmation: ConfirmationResult;
  scenario: 'RANGE_BOUND' | 'BULLISH' | 'BEARISH' | 'NO_SETUP';
  anchors: { shortCall: number | null; shortPut: number | null };
  structuralInvalidation: { upper: number | null; lower: number | null };
  reasons: string[];
  degraded: boolean;
}

export interface StrategySetupProposal {
  tradeable: boolean;
  reason?: string;
  expirySelection?: {
    expiry: string | null;
    dte: number;
    isWeekly: boolean;
    isMonthly: boolean;
    accepted: boolean;
    reason: string;
  };
  structure?: MultiTimeframeStructure;
  signal?: {
    strategy: string;
    action: string;
    confidence: number;
    eligible: boolean;
    reasonCodes: string[];
    suggestedExpiry?: string;
  };
  position?: {
    strategyId: string;
    strategyName: string;
    market: string;
    underlying: string;
    expiry: string;
    legs: Array<{
      legId: string;
      strike: number;
      optionType: 'CE' | 'PE';
      side: 'BUY' | 'SELL';
      quantity: number;
      entryPrice: number;
      currentPrice: number;
      lotCount: number;
    }>;
    entryPremium: number;
    maxProfit: number;
    maxLoss: number;
  };
  disclosure?: {
    strategyName: string;
    expiry: string;
    dte: number;
    netCredit: number;
    spreadWidth: number;
    maxProfit: number;
    maxLoss: number;
    breakevenUpper?: number;
    breakevenLower?: number;
    estimatedMargin: number;
    structuralStopLoss: {
      upper: number | null;
      lower: number | null;
      description: string;
    };
    isHedged: boolean;
    warnings: string[];
  };
  signals?: any[];
}

export interface PortfolioGreeks {
  delta: number;
  gamma: number;
  theta: number;
  vega: number;
  maxDefinedRisk: number;
}

export interface CampaignLeg {
  legId: string;
  side: 'BUY' | 'SELL';
  optionType: 'CE' | 'PE';
  strike: number;
  quantity: number;
  lotCount: number;
  entryPrice: number;
  currentPrice: number;
  pnl: number;
  greeks?: {
    delta?: number;
    gamma?: number;
    theta?: number;
    vega?: number;
  };
}

export interface ActiveCampaign {
  strategyId: string;
  strategyName: string;
  underlying: string;
  market: string;
  status: string;
  expiry: string;
  dteRemaining: number;
  entryTime: string;
  holdingDays: number;
  entryPremium: number;
  currentValue: number;
  maxProfit: number;
  maxLoss: number;
  unrealizedPnl: number;
  thetaDecayProgress: number;
  breakevenUpper?: number;
  breakevenLower?: number;
  greeks: {
    delta: number;
    gamma: number;
    theta: number;
    vega: number;
  };
  legs: CampaignLeg[];
  spotVsRange: {
    currentSpot: number;
    shortStrike: number;
    wingStrike: number;
    structuralStop: number;
  };
}

export interface ExpiryLadderItem {
  expiry: string;
  dte: number;
  positionCount: number;
  totalRisk: number;
  totalTheta: number;
}

export interface CapitalSummary {
  totalMargin: number;
  usedMargin: number;
  freeMargin: number;
  strategyMix: Record<string, number>;
}

export interface ClosedCampaign {
  strategyId: string;
  strategyName: string;
  underlying: string;
  entryTime: string;
  exitTime: string;
  holdingDays: number;
  entryCredit: number;
  exitCost: number;
  realizedPnl: number;
  maxProfit: number;
  maxLoss: number;
  outcome: 'MAX_PROFIT' | 'PARTIAL_PROFIT' | 'STOP_LOSS' | 'EXPIRY' | 'EMERGENCY';
  legs: any[];
}

export interface EquityCurvePoint {
  date: string;
  pnl: number;
  cumulativePnl: number;
  strategyName: string;
}

export interface StrategyStatItem {
  strategyName: string;
  totalCampaigns: number;
  wins: number;
  losses: number;
  winRate: number;
  totalPnl: number;
  avgPnl: number;
  avgHoldingDays: number;
}

export interface CommandCenterData {
  success: boolean;
  timestamp: string;
  systemMode: 'LIVE' | 'PAPER' | 'MONITOR_ONLY';
  broker: string;
  session: {
    sessionId?: string;
    currentState: string;
    date?: string;
    startTime?: string;
    underlying?: string;
  };
  portfolioGreeks: PortfolioGreeks;
  activeCampaigns: ActiveCampaign[];
  expiryLadder: ExpiryLadderItem[];
  capitalSummary: CapitalSummary;
  closedCampaigns: ClosedCampaign[];
  equityCurve: EquityCurvePoint[];
  strategyStats: StrategyStatItem[];
  rejectedSetups: any[];
  structureSummary?: {
    scenario: string;
    primaryBias?: string;
    primaryEvent?: string;
    macroBias?: string;
    /** True when candles were missing or stale: NO_SETUP then means "no data", not "no setup". */
    degraded?: boolean;
    /** Why the structure resolved as it did (most useful when scenario is NO_SETUP). */
    reason?: string;
    evaluatedAt?: string;
  } | null;
}

